import React, { useState, useEffect, useCallback, useRef } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import ClipLoader from "react-spinners/ClipLoader";
import { Plus } from "lucide-react";
import { useParams } from "react-router-dom";
import PostActions from "../../components/LikeCommentActions";
import DropdownActions from "../../components/DropdownActions";
import FileViewer from "../marketplace/FileViewer";


const Images = () => {
  const { userId: id } = useParams();
  const queryClient = useQueryClient();
  const [selectedImage, setSelectedImage] = useState(null);
  const [description, setDescription] = useState("");
  const [previewUrl, setPreviewUrl] = useState(null);
  const [deleteImageId, setDeleteImageId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const initialUserId = localStorage.getItem("user_id");
  const [currentUserId, setCurrentUserId] = useState(initialUserId);
  const [editMode, setEditMode] = useState(false);
  const [editImageId, setEditImageId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editImageFile, setEditImageFile] = useState(null);
  const [editDescription, setEditDescription] = useState("");
  const [editPreviewUrl, setEditPreviewUrl] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const token = localStorage.getItem("access_token");

  const fetchCurrentUser = useCallback(async () => {

    try {
      const res = await fetch(`${BASE_URL}/user/profile/create-profile/`, {
        headers: { Authorization: `Bearer ${token}`,  "x-api-key": process.env.REACT_APP_X_API_KEY, },
      });
      const data = await res.json();
      if (res.ok) {
        setCurrentUserId(data.data?.user);
        localStorage.setItem("user_id", data.data?.user);
      } else {
        console.error("");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error getting user profile.");
    }
  }, [BASE_URL, token]);

  // const fetchGalleryImages = async ({ pageParam = 1 }) => {
  //   let url = null;
  //   if(id) {
  //     url = `${BASE_URL}/user/profile/all-profiles?id=${id}`;
  //   } else {
  //     url = `${BASE_URL}/user/gallery/gallery-images/?page=${pageParam}`;
  //   }

  //   const res = await fetch(url, {
  //     headers: {
  //       Authorization: `Bearer ${token}`,
  //     },
  //   });

  //   const data = await res.json();

  //   if (!res.ok) {
  //     throw new Error("Failed to fetch images");
  //   }

  //   const sorted = (data.results || []).sort(
  //     (a, b) => new Date(b.created_at) - new Date(a.created_at)
  //   );
  //   return {
  //     images: sorted,
  //     count: data.count,
  //     currentPage: pageParam,
  //     next: data.next ? true : false,
  //   };
  // };

  const fetchGalleryImages = async ({ pageParam = 1 }) => {
    if (id) {
      const url = `${BASE_URL}/user/profile/all-profiles/?id=${id}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}`, "x-api-key": process.env.REACT_APP_X_API_KEY },
      });
      const json = await res.json();

      if (!res.ok) throw new Error("Failed to fetch images");

      const images = json.data?.gallery_images || [];
      const sortedImages = (images || [])
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .map((img) => ({
          ...img,
          isLiked: img.is_liked,
        }));

      return {
        images: sortedImages,
        count: images.length,
        currentPage: 1,
        next: false,
      };
    } else {
      const url = `${BASE_URL}/user/gallery/gallery-images/?page=${pageParam}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}`,"x-api-key": process.env.REACT_APP_X_API_KEY },
      });
      const data = await res.json();

      if (!res.ok) throw new Error("Failed to fetch images");

      const sorted = (data.results || []).sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );

      return {
        images: sorted,
        count: data.count,
        currentPage: pageParam,
        next: data.next ? true : false,
      };
    }
  };

  useEffect(() => {
    fetchCurrentUser();
  }, [fetchCurrentUser]);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith("image/")) {
      setSelectedImage(file);
      setPreviewUrl(URL.createObjectURL(file));
    } else {
      toast.error("Please select a valid image file");
      event.target.value = null;
    }
  };

  const handleUploadClick = () => {
    document.getElementById("image-upload-input").click();
  };

  // const handleUpload = async () => {
  //   if (!selectedImage || !description.trim()) {
  //     toast.warning("Please select an image and enter description.");
  //     return;
  //   }

  //   const formData = new FormData();
  //   formData.append("image", selectedImage);
  //   formData.append("description", description);

  //   try {
  //     setIsUploading(true);
  //     const res = await fetch(`${BASE_URL}/user/gallery/gallery-images/`, {
  //       method: "POST",
  //       headers: { Authorization: `Bearer ${token}` },
  //       body: formData,
  //     });

  //     const data = await res.json();

  //     if (res.ok) {
  //       toast.success("Image uploaded successfully!");
  //       setSelectedImage(null);
  //       setDescription("");
  //       setPreviewUrl(null);

  //       // fetchImages();
  //       queryClient.setQueryData(["images"], (oldData) => {
  //         if (!oldData) return oldData;

  //         return {
  //           pageParams: oldData.pageParams,
  //           pages: oldData.pages.map((page, idx) =>
  //             idx === 0
  //               ? {
  //                   ...page,
  //                   images: [data?.data, ...page.images],
  //                 }
  //               : page
  //           ),
  //         };
  //       });
  //     } else {
  //       toast.error(data.message || "Failed to upload image.");
  //     }
  //   } catch (err) {
  //     console.error(err);
  //     toast.error("Upload failed. Please try again.");
  //   } finally {
  //     setIsUploading(false);
  //   }
  // };

  const handleUpload = async () => {
    if (!selectedImage) {
      toast.warning("Please select an image.");
      return;
    }
    const formData = new FormData();
    formData.append("image", selectedImage);
    formData.append("description", description);
    try {
      setIsUploading(true);
      const res = await fetch(`${BASE_URL}/user/gallery/gallery-images/`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`,"x-api-key": process.env.REACT_APP_X_API_KEY },
        body: formData,
      });
      const data = await res.json();

      if (res.ok) {
        toast.success("Image uploaded successfully!");
        queryClient.setQueryData(["images", id], (oldData) => {
          if (!oldData) return oldData;
          return {
            pageParams: oldData.pageParams,
            pages: oldData.pages.map((page, idx) =>
              idx === 0
                ? { ...page, images: [data.data, ...page.images] }
                : page
            ),
          };
        });

        setSelectedImage(null);
        setDescription("");
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
        document.getElementById("image-upload-input").value = null;
      } else {
        toast.error(data.message || "Failed to upload image.");
      }
    } catch (err) {
      console.error(err);
      toast.error("Upload failed. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleEditClick = (image) => {
    setEditMode(true);
    setEditImageId(image.id);
    setEditDescription(image.description || "");
    setEditPreviewUrl(image.image);
  };

  const handleEditFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith("image/")) {
      setEditImageFile(file);
      setEditPreviewUrl(URL.createObjectURL(file));
    } else {
      toast.error("Please select a valid image file.");
    }
  };

  const handleEditSubmit = async () => {
    if (!editImageId) {
      toast.warning("Please enter description.");
      return;
    }

    const formData = new FormData();
    formData.append("id", editImageId);
    formData.append("description", editDescription);
    if (editImageFile) {
      formData.append("image", editImageFile);
    }
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/user/gallery/gallery-images/`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}`,"x-api-key": process.env.REACT_APP_X_API_KEY },
        body: formData,
      });

      const data = await res.json();
      if (res.ok) {
        toast.success("Image updated successfully!");
        setEditMode(false);
        setEditImageId(null);
        setEditImageFile(null);
        setEditDescription("");
        setEditPreviewUrl(null);

        queryClient.setQueryData(["images", id], (oldData) => {
          if (!oldData) return oldData;

          return {
            pageParams: oldData.pageParams,
            pages: oldData.pages.map((page) => ({
              ...page,
              images: page.images.map((img) =>
                img.id === editImageId
                  ? {
                    ...img,
                    description: editDescription,
                    image: editImageFile ? editPreviewUrl : img.image, // use preview URL immediately
                  }
                  : img
              ),
            })),
          };
        });
      } else {
        toast.error(data.message || "Failed to update image.");
      }
    } catch (error) {
      console.error(error);
      toast.error("Update failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (imageId) => {
    setDeleteImageId(imageId);
    setShowDeleteModal(true);
  };

  // const confirmDelete = async () => {
  //   try {
  //     const res = await fetch(`${BASE_URL}/user/gallery/gallery-images/`, {
  //       method: "DELETE",
  //       headers: {
  //         "Content-Type": "application/json",
  //         Authorization: `Bearer ${token}`,
  //       },
  //       body: JSON.stringify({ id: deleteImageId }),
  //     });

  //     const data = await res.json();

  //     if (res.ok) {
  //       toast.success("Image deleted successfully!");
  //       setShowDeleteModal(false);
  //       setDeleteImageId(null);

  //       queryClient.setQueryData(["images"], (oldData) => {
  //         if (!oldData) return oldData;

  //         return {
  //           pageParams: oldData.pageParams,
  //           pages: oldData.pages.map((page) => ({
  //             ...page,
  //             images: page.images.filter((img) => img.id !== deleteImageId),
  //           })),
  //         };
  //       });
  //     } else {
  //       toast.error(data?.message || "Failed to delete image.");
  //     }
  //   } catch (error) {
  //     console.error(error);
  //     toast.error("Deletion failed.");
  //   }
  // };

  const confirmDelete = async () => {
    if (!deleteImageId) return;
    const previousData = queryClient.getQueryData(["images", id]);

    queryClient.setQueryData(["images", id], (oldData) => {
      if (!oldData) return oldData;
      return {
        pageParams: oldData.pageParams,
        pages: oldData.pages.map((page) => ({
          ...page,
          images: page.images.filter((img) => img.id !== deleteImageId),
        })),
      };
    });

    setShowDeleteModal(false);
    setDeleteImageId(null);

    try {
      setLoading(true);
      const res = await fetch(`${BASE_URL}/user/gallery/gallery-images/`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "x-api-key": process.env.REACT_APP_X_API_KEY
        },
        body: JSON.stringify({ id: deleteImageId }),
      });

      const data = await res.json();

      if (res.ok) {
        toast.success("Image deleted successfully!");
      } else {
        toast.error(data?.message || "Failed to delete image.");
        queryClient.setQueryData(["images", id], previousData);
      }
    } catch (error) {
      console.error(error);
      toast.error("Deletion failed.");
      queryClient.setQueryData(["images", id], previousData); // rollback
    } finally {
      setLoading(false);
    }
  };

  const { data, fetchNextPage, hasNextPage, isLoading, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ["images", id],
      queryFn: fetchGalleryImages,
      getNextPageParam: (lastPage, pages) => {
        return lastPage.next ? pages.length + 1 : undefined;
      },
      initialPageParam: 1,
    });

  const observerRef = useRef();
  const lastImageRef = useCallback(
    (node) => {
      if (isFetchingNextPage) return;
      if (observerRef.current) observerRef.current.disconnect();

      observerRef.current = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;
          if (entry.isIntersecting && hasNextPage) {
            fetchNextPage();
          }
        },
        {
          root: null,
          rootMargin: "100px",
          threshold: 0,
        }
      );

      if (node) observerRef.current.observe(node);
    },
    [isFetchingNextPage, hasNextPage, fetchNextPage]
  );
  const imagesData = data?.pages.flatMap((page) => page.images) || [];

  return (
    <div className="w-full flex items-center justify-center">
      <ToastContainer />
      <div className="w-full max-w-[1050px]">
     <div className="w-full flex justify-between items-center">
  <h2 className="text-lg font-bold text-gray-900">
    Upload Conference Images and Announcements
  </h2>

  {(id === currentUserId || !id) && (
    <button
      className="ml-4 shrink-0 bg-[#004182] text-white p-3 rounded-full font-medium cursor-pointer border border-[#00449d] flex items-center justify-center"
      onClick={handleUploadClick}
    >
      <Plus size={18} />
    </button>
  )}

  <input
    type="file"
    id="image-upload-input"
    accept="image/*"
    className="hidden"
    onChange={handleFileChange}
  />
</div>


     {selectedImage && (
  <div className="mt-5 p-4 border-2 border-dashed border-gray-300 rounded-xl bg-slate-50 flex flex-col gap-4">
    
    <img
      src={previewUrl}
      alt="Preview"
      className="w-[120px] h-[120px] rounded-xl object-cover object-center border border-gray-300 mx-auto"
    />

    <textarea
      placeholder="Enter image description..."
      value={description}
      onChange={(e) => setDescription(e.target.value)}
      className="w-full min-h-[60px] px-4 py-3 text-sm resize-y border border-gray-300 rounded-xl focus:outline-none focus:border-[#0f6abf] focus:ring-2 focus:ring-[#0f6abf]/20 transition-all placeholder:text-gray-400"
    />

    <button
      onClick={handleUpload}
      disabled={isUploading}
      className="h-[40px] px-6 flex items-center justify-center rounded-xl text-white text-sm font-medium capitalize bg-[#0f6abf] hover:bg-[#0a5ca8] disabled:opacity-50 disabled:cursor-not-allowed transition-colors mx-auto"
    >
      {isUploading ? (
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
          Uploading...
        </div>
      ) : (
        "Submit"
      )}
    </button>

  </div>
)}
        <div>
          {isLoading ? (
            <div className="loading-snippets">
              <ClipLoader color="#4A90E2" size={40} />
            </div>
          ) : imagesData.length === 0 ? (
             <div className="mt-6 flex flex-col justify-center items-center py-12 bg-white rounded-xl border border-gray-200">
    <div className="w-16 h-16 bg-[#e8f0f7] rounded-xl flex items-center justify-center mb-4">
      <svg 
        className="w-8 h-8 text-[#0f6abf]" 
        fill="none" 
        stroke="currentColor" 
        viewBox="0 0 24 24" 
        xmlns="http://www.w3.org/2000/svg"
      >
        <path 
          strokeLinecap="round" 
          strokeLinejoin="round" 
          strokeWidth="2" 
          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" 
        />
      </svg>
    </div>
    <h3 className="text-lg font-semibold text-gray-900 mb-2">No images yet</h3>
    <p className="text-gray-500 text-sm mb-4">Upload the first conference images and announcements to get started</p>
  </div>
          ) : (
        <div className="grid grid-cols-1 gap-4 pt-5">
  {imagesData &&
    imagesData.map((image, index) => {
      return (
        <div
          key={`image-${index}`}
          className="relative flex flex-col rounded-xl border border-gray-200 bg-white p-5"
        >
          {/* Edit/Dropdown Button */}
          {Number(image.user) === Number(currentUserId) && (
            <div className="absolute right-0 top-[-25px] z-10">
              <DropdownActions
                onEdit={() => handleEditClick(image)}
                onDelete={() => handleDeleteClick(image.id)}
              />
            </div>
          )}

          {/* Edit Mode */}
          {editMode && editImageId === image.id && (
            <div className="mt-4 mb-6 rounded-xl border-2 border-dashed border-gray-300 bg-slate-50 p-5">
              <div className="flex gap-4">
                <img
                  src={editPreviewUrl}
                  alt="Edit Preview"
                  className="h-32 w-32 rounded-lg object-cover border border-gray-300"
                />

                <div className="flex-1">
                  <textarea
                    placeholder="Edit image description..."
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                    className="w-full min-h-[80px] p-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#0f6abf] focus:ring-2 focus:ring-[#0f6abf]/20 resize-y transition-all"
                  />

                  <div className="mt-3">
                    <label className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors cursor-pointer">
                      <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      Change Image
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleEditFileChange}
                        className="hidden"
                      />
                    </label>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 mt-4">
                <button
                  onClick={handleEditSubmit}
                  disabled={loading}
                  className="flex-1 py-2.5 bg-[#0f6abf] text-white text-sm font-medium rounded-lg hover:bg-[#0a5ca8] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Saving...
                    </>
                  ) : (
                    "Save Changes"
                  )}
                </button>

                <button
                  onClick={() => {
                    setEditMode(false);
                    setEditImageId(null);
                    setEditImageFile(null);
                    setEditDescription("");
                    setEditPreviewUrl(null);
                  }}
                  className="flex-1 py-2.5 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Description */}
          <div className="mb-4">
            {(image.description || "")
              .split("\n")
              .map((line, i) => (
                <div key={i} className="text-sm text-gray-700 leading-relaxed mb-2">
                  {line
                    .split(/(#[a-zA-Z0-9_]+)/g)
                    .map((part, idx) =>
                      part.startsWith("#") ? (
                        <span
                          key={idx}
                          className="text-[#0f6abf] font-medium"
                        >
                          {part}
                        </span>
                      ) : (
                        <React.Fragment key={idx}>{part}</React.Fragment>
                      )
                    )}
                </div>
              ))}
          </div>

          {/* Image */}
          <FileViewer
            publication_type="image"
            file={image.image}
            name={image.description || `Gallery ${index}`}
          />


          {/* Actions */}
          <div className="mt-4 pt-4">
            <PostActions
              postId={image.id}
              type="image"
              is_Liked={image.is_liked === true}
              likeCount={image.total_likes}
              currentUser={currentUserId}
            />
          </div>
        </div>
      );
    })}

  <div ref={lastImageRef} className="h-4" />

  {isFetchingNextPage && (
    <div className="flex justify-center py-8">
      <div className="w-8 h-8 border-2 border-[#0f6abf] border-t-transparent rounded-full animate-spin"></div>
    </div>
  )}
</div>

          )}
        </div>
      </div>

      {showDeleteModal && (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000] p-4">
    <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center">
          <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Delete Image</h3>
          <p className="text-sm text-gray-500 mt-0.5">This action cannot be undone</p>
        </div>
      </div>

      {/* Content */}
      <div className="mb-6">
        <h4 className="text-base font-medium text-gray-900 mb-2">
          Are you sure you want to delete this image?
        </h4>
        <p className="text-sm text-gray-600">
          The image will be permanently removed from your gallery.
        </p>
      </div>

      {/* Buttons */}
      <div className="flex gap-3">
        <button
          onClick={() => setShowDeleteModal(false)}
          className="flex-1 px-4 py-2.5 text-gray-700 font-medium rounded-xl hover:bg-gray-100 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={confirmDelete}
          disabled={loading}
          className={`flex-1 px-4 py-2.5 font-medium rounded-xl transition-colors flex items-center justify-center gap-2 ${
            loading
              ? 'bg-red-300 cursor-not-allowed'
              : 'bg-red-500 text-white hover:bg-red-600'
          }`}
        >
          {loading ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              Deleting...
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Delete Image
            </>
          )}
        </button>
      </div>
    </div>
  </div>
)}

    </div>
  );
};

export default Images;
